import {
  CourseEripBillMutation,
  useCourseEripBillMutation,
  useCourseOfferQuery,
} from "@/__generated__/graphql";
import LoadingView from "@/components/layouts/LoadingView";
import { AppModal } from "@/components/modals/AppModal/AppModal";
import { extractGraphQLErrorMessage } from "@/helpers";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import CourseEnrollView from "../views/CourseEnrollView";

// Пока ждём оплату — опрашиваем статус, чтобы сменить экран на «Вы записаны»,
// как только вебхук ЕРИП завершит запись (оплата приходит с задержкой в минуты).
const POLL_MS = 6000;

type Bill = CourseEripBillMutation["courseEripBill"];

export default function CourseEnroll({
  typeRecId,
  onClose,
}: {
  typeRecId: string;
  onClose: () => void;
}) {
  const [bill, setBill] = useState<Bill | null>(null);
  const [useBonus, setUseBonus] = useState(false);

  const { data, loading, refetch, startPolling, stopPolling } = useCourseOfferQuery({
    variables: { typeRecId },
    fetchPolicy: "cache-and-network",
  });
  const [issueBill, billState] = useCourseEripBillMutation();

  const offer = data?.courseOffer;
  const enrolled = Boolean(offer?.enrolled);
  const paying = Boolean(bill);

  // Поллинг + дозапрос при возврате в приложение из банка — только пока ждём оплату.
  useEffect(() => {
    if (!paying || enrolled) {
      stopPolling();
      return;
    }
    startPolling(POLL_MS);
    const onFocus = () => refetch();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      stopPolling();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [paying, enrolled, startPolling, stopPolling, refetch]);

  // «Оплатить онлайн»: выпускаем разовый ЕРИП-счёт на цену (QR/ссылки уже с
  // суммой) — он же сохраняет заявку, которую вебхук добьёт после оплаты.
  const onPayOnline = async () => {
    try {
      const { data: res } = await issueBill({ variables: { typeRecId, useBonus } });
      if (res?.courseEripBill) setBill(res.courseEripBill);
    } catch (error) {
      toast.error(extractGraphQLErrorMessage(error));
    }
  };

  const onBack = useCallback(() => setBill(null), []);

  return (
    <AppModal open onClose={onClose}>
      {!offer && loading ? (
        <LoadingView />
      ) : offer ? (
        <CourseEnrollView
          offer={offer}
          useBonus={useBonus}
          onToggleBonus={setUseBonus}
          onPayOnline={onPayOnline}
          requesting={billState.loading}
          paying={paying}
          onBack={onBack}
          bill={bill}
          onClose={onClose}
        />
      ) : null}
    </AppModal>
  );
}
